import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { RootState, useAppDispatch } from '../../../support/store';
import { useSelector } from 'react-redux';
import { GtmSolution } from '../../../types/go-to-market';
import { HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { placeholder } from '@harbor/elements/icons';
import StbMarkdown from '../../security-toolbox-ui/StbMarkdown';
import { useFetchSingleGtmArchitecture } from '../../../hooks/gtm-solution-hooks';
import { checkGtmSolutionsLoaded } from '../../../support/slices/gtmSolutionsSlice';
import { checkCiscoProductsLoaded } from '../../../support/slices/productsSlice';
import ArchitectureComponentMap from './ArchitectureComponentMap';
import ArchitectureUseCaseList from './ArchitectureUseCaseList';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';


const ArchitectureDetail = () => {
  const { id } = useParams();
  const solutionId: GtmSolution['id'] = parseInt(id);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(checkGtmSolutionsLoaded());
    dispatch(checkCiscoProductsLoaded());
  }, [dispatch]);
  const solutionSummary: GtmSolution = useSelector((state: RootState) => state.gtmSolutions.architectures
    .find((product) => product.id === solutionId)
  );
  const solutionSummaryLoading = useSelector((state: RootState) => state.gtmSolutions.isLoading);
  const solutionSummaryInitialized = useSelector((state: RootState) => state.gtmSolutions.isInitialized);

  const { data: solutionDetail, isLoading: solutionDetailLoading } = useFetchSingleGtmArchitecture(solutionId);
  const solution = solutionDetail ?? solutionSummary;
  const solutionLoading = solutionSummaryLoading || !solutionSummaryInitialized;

  const productMap = useSelector((state: RootState) => state.products.ciscoProductMap || {});
  
  return (
    <section className='mx-4 mt-4'>
      {solutionLoading
        ? (
            <HbrSpinner>Loading Architecture... </HbrSpinner>
          )
        : (
            <>
              <NavHistoryTracker title={solution.name} />
              <header className="my-4 flex gap-2 items-center">
                <div className="rounded-xl bg-cisco-blue-60 w-14 h-14 box-border flex items-center justify-center">
                  <HbrIcon
                    asset={placeholder}
                    size="36px"
                    className="text-white h-0 w-0"
                  />
                </div>

                <div>
                  <h4 className="mt-0 mb-0 text-cisco-blue-60 leading-6">
                    Architecture
                  </h4>
                  <h1 className="mt-0 mb-2 text-gray-60 font-semibold leading-5">
                    {solution ? solution.name : ''}
                  </h1>
                </div>
              </header>

              <div id="architecture-description" className="mb-6">
                {solution?.description
                  ? <StbMarkdown>{solution?.description}</StbMarkdown>
                  : <div className="italic text-gray-50">No description found for this solution.</div>
                }
              </div>

              <div id="architecture-component-map" className="mb-6 -mx-4">
                {solution
                  ? (
                      <ArchitectureComponentMap
                        components={solution?.component ?? []}
                        productMapMaster={productMap}
                        solutionDetailLoading={solutionDetailLoading}
                      />
                    )
                  : (
                      <div className="italic text-gray-50">Architecture not found.</div>
                    )
                }
              </div>

              <div id="architecture-use-cases">
                <ArchitectureUseCaseList
                  useCases={solution?.use_case ?? []}
                  productMap={productMap}
                />
              </div>
            </>
          )
      }
    </section>
  ) 
};

export default ArchitectureDetail;
